import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';

interface VideoPlayerProps {
  videoUrl: string;
  currentTime?: number;
  onTimeUpdate?: (time: number) => void;
  className?: string;
  clipStart?: number;
  clipEnd?: number;
}

export interface VideoPlayerRef {
  seekTo: (time: number) => void;
  playClip: (startTime: number, endTime: number) => void;
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(({ 
  videoUrl, 
  currentTime, 
  onTimeUpdate,
  className = '',
  clipStart,
  clipEnd
}, ref) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [currentVideoTime, setCurrentVideoTime] = React.useState(0);
  const [seeking, setSeeking] = React.useState(false);
  const clipIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const clipEndTimeRef = useRef<number | null>(null);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    seekTo: (time: number) => {
      // console.log(`Enhanced VideoPlayer seekTo called with time: ${time}`);
      if (videoRef.current) {
        const video = videoRef.current;
        setSeeking(true);
        
        const doSeek = () => {
          video.currentTime = time;
          console.log(`Seeked to: ${time}s, actual time: ${video.currentTime}s`);
          setTimeout(() => setSeeking(false), 500);
        };
        
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA
          doSeek();
        } else {
          video.addEventListener('canplay', doSeek, { once: true });
        }
      }
    },
    playClip: (startTime: number, endTime: number) => {
      // console.log(`Enhanced VideoPlayer playClip called: ${startTime}s-${endTime}s`);
      if (videoRef.current) {
        const video = videoRef.current;
        
        // Clear any existing interval
        if (clipIntervalRef.current) {
          clearInterval(clipIntervalRef.current);
          clipIntervalRef.current = null;
        }
        
        clipEndTimeRef.current = endTime;
        setSeeking(true);
        
        const startPlayback = () => {
          video.currentTime = startTime;
          
          // Wait for seek to complete, then play
          const onSeeked = () => {
            console.log(`Seeked to start time: ${startTime}s, actual: ${video.currentTime}s`);
            video.play();
            setSeeking(false);
            
            // Set up precise interval to check for end time
            clipIntervalRef.current = setInterval(() => {
              const current = video.currentTime;
              if (current >= endTime) {
                video.pause();
                if (clipIntervalRef.current) {
                  clearInterval(clipIntervalRef.current);
                  clipIntervalRef.current = null;
                }
                clipEndTimeRef.current = null;
                // console.log(`Clip ended at ${endTime}s, actual: ${current}s`);
              }
            }, 50); // Check every 50ms for more precision
            
            video.removeEventListener('seeked', onSeeked);
          };
          
          video.addEventListener('seeked', onSeeked, { once: true });
        };
        
        if (video.readyState >= 2) {
          startPlayback();
        } else {
          video.addEventListener('canplay', startPlayback, { once: true });
        }
      }
    }
  }), []);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (clipIntervalRef.current) {
        clearInterval(clipIntervalRef.current);
      }
    };
  }, []);

  // Seek to specific time when currentTime prop changes
  useEffect(() => {
    if (videoRef.current && currentTime !== undefined) {
      const video = videoRef.current;
      
      const seekToTime = () => {
        if (video.readyState >= 2) {
          // console.log(`Seeking to time via prop: ${currentTime}s`);
          setSeeking(true);
          video.currentTime = currentTime;
          setTimeout(() => setSeeking(false), 500);
        } else {
          video.addEventListener('canplay', () => {
            console.log(`Video ready, seeking to: ${currentTime}s`);
            setSeeking(true);
            video.currentTime = currentTime;
            setTimeout(() => setSeeking(false), 500);
          }, { once: true });
        }
      };
      
      seekToTime();
    }
  }, [currentTime]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      setCurrentVideoTime(time);
      onTimeUpdate?.(time);
      
      // Additional check for clip end time (backup to interval)
      if (clipEndTimeRef.current && time >= clipEndTimeRef.current) {
        videoRef.current.pause();
        if (clipIntervalRef.current) {
          clearInterval(clipIntervalRef.current);
          clipIntervalRef.current = null;
        }
        const endTime = clipEndTimeRef.current;
        clipEndTimeRef.current = null;
        // console.log(`Clip ended via timeupdate at ${endTime}s`);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      console.log('Enhanced video player loaded metadata');
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentVideoTime(time);
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`bg-black rounded-lg overflow-hidden relative ${className}`}>
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onLoadStart={() => console.log('Enhanced video loading:', videoUrl)}
        preload="metadata"
      />
      
      {/* Seeking Indicator */}
      {seeking && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-white/90 px-4 py-2 rounded-md text-black font-medium">
            {t('analytics.seekingTo')} {formatTime(currentTime || 0)}...
          </div>
        </div>
      )}
      
      {/* Custom Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        {/* Progress Bar */}
        <div className="mb-3">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentVideoTime}
            onChange={handleSeek}
            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #1b3968 0%, #1b3968 ${(currentVideoTime / duration) * 100}%, #6b7280 ${(currentVideoTime / duration) * 100}%, #6b7280 100%)`
            }}
          />
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlayPause}
              className="text-white hover:text-[#1b3968] transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-6 w-6" />
              ) : (
                <Play className="h-6 w-6" />
              )}
            </button>

            <button
              onClick={toggleMute}
              className="text-white hover:text-[#1b3968] transition-colors"
            >
              {isMuted ? (
                <VolumeX className="h-5 w-5" />
              ) : (
                <Volume2 className="h-5 w-5" />
              )}
            </button>

            <div className="text-white text-sm">
              {formatTime(currentVideoTime)} / {formatTime(duration)}
            </div>
          </div>

          <button
            onClick={toggleFullscreen}
            className="text-white hover:text-[#1b3968] transition-colors"
          >
            <Maximize className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;